"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp } from "lucide-react";
const trackRecordData = [{
  ticker: "COIN",
  name: "Coinbase",
  entry: "~$45.00",
  return: "+750%"
}, {
  ticker: "TSLA",
  name: "Tesla",
  entry: "~$105.00",
  return: "+300%"
}, {
  ticker: "GLXY",
  name: "Galaxy Digital",
  entry: "~$2.00 CAD",
  return: "+1,300%"
}, {
  ticker: "HOOD",
  name: "Robinhood",
  entry: "~$7.00",
  return: "+1,200%"
}, {
  ticker: "PLTR",
  name: "Palantir",
  entry: "~$6.00",
  return: "+2,000%"
}];
export default function TheTrackRecord() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-track-record" className="bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: `linear-gradient(to right, var(--color-foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--color-foreground) 1px, transparent 1px)`,
      backgroundSize: '40px 40px'
    }} aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-3xl md:text-5xl font-bold font-default mb-6 tracking-tight">
            The Track <span className="text-primary">Record</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-light">
            Independent, high-conviction calls that have consistently delivered asymmetric returns. Data-driven entries, multi-bagger exits.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {trackRecordData.map((item, i) => <TrackRecordCard key={i} item={item} index={i} />)}
        </div>
      </div>
    </section>;
}
function TrackRecordCard({
  item,
  index
}: {
  item: typeof trackRecordData[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full bg-foreground/5 border-border/30 hover:border-primary hover:shadow-[0_0_20px_var(--color-primary)] transition-all duration-500 backdrop-blur-sm overflow-hidden group rounded-xl">
        <CardHeader className="pb-4 border-b border-border/10">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="font-mono text-2xl md:text-3xl font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                ${item.ticker}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1 font-medium tracking-wide">{item.name}</p>
            </div>
            <div className="p-2.5 bg-background/50 rounded-lg group-hover:bg-primary/10 transition-colors duration-300 border border-border/20 group-hover:border-primary/30">
              <TrendingUp className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 flex flex-col gap-6">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-widest font-semibold mb-1.5 opacity-80">Entry Price</p>
            <p className="text-xl text-foreground font-medium font-mono">{item.entry}</p>
          </div>
          <div className="mt-auto pt-2">
            <p className="text-xs text-muted-foreground uppercase tracking-widest font-semibold mb-2 opacity-80">Return</p>
            <p className="text-5xl md:text-6xl font-bold text-chart-2 font-mono tracking-tighter" style={{
            textShadow: '0 0 24px var(--color-chart-2)'
          }}>
              {item.return}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>;
}